/**
 * Device pairing — one-time email + password + 6-digit PIN. WebAuthn
 * enrollment is opt-in; we offer it after the device is paired so the
 * user has time to read the prompt rather than being ambushed during
 * sign-in.
 *
 * On success, sets the in-memory session, persists the wrapped token
 * to Dexie, and routes to /events.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { pairDevice, isPlatformWebAuthnAvailable } from '@argos/auth-client';
import { authHttp } from '../api/client';
import { DexieDeviceStore } from '../auth/dexie-store';
import { setSession } from '../auth/session';

const PWA_HOSTNAME =
  typeof window !== 'undefined' ? window.location.hostname : 'localhost';
const RP_ID = PWA_HOSTNAME;

interface PairFormFields {
  email: string;
  password: string;
  pin: string;
  pinConfirm: string;
  deviceName: string;
}

function detectPlatform(): 'ios-pwa' | 'android-pwa' | 'web' {
  if (typeof navigator === 'undefined') return 'web';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios-pwa';
  if (/Android/i.test(ua)) return 'android-pwa';
  return 'web';
}

export function PairPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<PairFormFields>({
    mode: 'onBlur',
    defaultValues: {
      deviceName: typeof navigator !== 'undefined' && navigator.platform
        ? `${navigator.platform} • ARGOS Field`
        : 'ARGOS Field device',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    setError(null);
    try {
      const webauthnAvailable = await isPlatformWebAuthnAvailable();
      const result = await pairDevice({
        email: values.email.trim(),
        password: values.password,
        pin: values.pin,
        deviceName: values.deviceName.trim().slice(0, 200) || 'ARGOS Field device',
        platform: detectPlatform(),
        http: authHttp,
        store: new DexieDeviceStore(),
        rpId: RP_ID,
        rpName: 'ARGOS Field',
        // Skip WebAuthn on first launch even when available — gives the
        // tech a clean PIN-paired baseline they can fall back to. The
        // settings page (Phase 3.22) will offer to upgrade to biometric
        // unlock as a follow-on.
        enableWebAuthn: false && webauthnAvailable,
      });

      // Pair returns the just-issued device token in the wrapped form
      // already — we don't have the unwrapped one in hand. Push the
      // user through a fresh unlock to populate the session.
      setSession(null);
      void result; // satisfy linter
      navigate('/unlock', { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Pairing failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  });

  const pin = watch('pin');

  return (
    <div className="flex-1 flex flex-col items-center p-6">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-semibold mb-1">Pair this device</h1>
        <p className="text-mono-400 text-sm mb-6">
          Sign in once to set up offline access. You'll unlock with a
          6-digit PIN on subsequent launches.
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
              Email
            </label>
            <input
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              className="input-field"
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /.+@.+\..+/, message: 'Looks invalid' },
              })}
            />
            {errors.email && (
              <p className="text-accent-red text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
              Password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              className="input-field"
              {...register('password', { required: 'Password is required' })}
            />
            {errors.password && (
              <p className="text-accent-red text-sm mt-1">{errors.password.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
              6-digit PIN
            </label>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={6}
              className="input-field tracking-widest text-center text-lg"
              {...register('pin', {
                required: 'PIN is required',
                minLength: { value: 6, message: 'PIN must be 6 digits' },
                maxLength: { value: 6, message: 'PIN must be 6 digits' },
                pattern: { value: /^\d{6}$/, message: 'Digits only' },
              })}
            />
            {errors.pin && (
              <p className="text-accent-red text-sm mt-1">{errors.pin.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
              Confirm PIN
            </label>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={6}
              className="input-field tracking-widest text-center text-lg"
              {...register('pinConfirm', {
                required: 'Confirm PIN',
                validate: (v) => v === pin || 'PINs do not match',
              })}
            />
            {errors.pinConfirm && (
              <p className="text-accent-red text-sm mt-1">{errors.pinConfirm.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
              Device name (optional)
            </label>
            <input type="text" className="input-field" {...register('deviceName')} />
          </div>

          {error && (
            <div className="text-accent-red text-sm border border-red-900/40 rounded p-2 bg-red-950/20">
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? 'Pairing…' : 'Pair device'}
          </button>
        </form>
      </div>
    </div>
  );
}
